import { useMemo } from 'react';
import { PositionSnapshot } from '../../../types/trading';
import { normalizeSymbol } from '../../../components/trading-dashboard/portfolioValue';
import { sortPositionsByTradeAtDesc } from './formatters';

export interface PositionGroup {
  symbol: string;
  lots: PositionSnapshot[];
  aggregate: PositionSnapshot;
}

export function useGroupedPositions(positions: PositionSnapshot[]): PositionGroup[] {
  return useMemo(() => {
    const bySymbol = new Map<string, PositionSnapshot[]>();
    positions.forEach((lot) => {
      const symbol = normalizeSymbol(lot.symbol) || lot.symbol;
      if (!symbol) return;
      const existing = bySymbol.get(symbol);
      if (existing) existing.push(lot);
      else bySymbol.set(symbol, [lot]);
    });

    const groups = Array.from(bySymbol.entries()).map(([symbol, lots]) => {
      const sortedLots = sortPositionsByTradeAtDesc(lots);
      const netQty = sortedLots.reduce((sum, lot) => sum + lot.net_qty, 0);
      const totalAbsQty = sortedLots.reduce((sum, lot) => sum + Math.abs(lot.net_qty), 0);
      const totalCostBasis = sortedLots.reduce(
        (sum, lot) => sum + Math.abs(lot.net_qty) * lot.avg_price,
        0,
      );
      const aggregateAvg = totalAbsQty > 0 ? totalCostBasis / totalAbsQty : 0;
      const latestTradeAt = sortedLots[0]?.trade_at ?? '';
      const latestUpdatedAt = sortedLots.reduce((latest, lot) => {
        const latestTs = Number.isNaN(Date.parse(latest ?? '')) ? 0 : Date.parse(latest ?? '');
        const lotTs = Number.isNaN(Date.parse(lot.updated_at ?? '')) ? 0 : Date.parse(lot.updated_at ?? '');
        return lotTs > latestTs ? lot.updated_at : latest;
      }, sortedLots[0]?.updated_at ?? '');

      const aggregate: PositionSnapshot = {
        lot_id: `group:${symbol}`,
        user_uuid: sortedLots[0]?.user_uuid ?? '',
        symbol,
        net_qty: netQty,
        closed_qty: 0,
        avg_price: aggregateAvg,
        realized_pnl: sortedLots.reduce((sum, lot) => sum + lot.realized_pnl, 0),
        closed_at: null,
        last_price: null,
        unrealized_pnl: null,
        trade_at: latestTradeAt,
        updated_at: latestUpdatedAt,
      };

      return { symbol, lots: sortedLots, aggregate };
    });

    return groups.sort((a, b) => {
      const safeA = Number.isNaN(Date.parse(a.aggregate.trade_at ?? ''))
        ? 0
        : Date.parse(a.aggregate.trade_at ?? '');
      const safeB = Number.isNaN(Date.parse(b.aggregate.trade_at ?? ''))
        ? 0
        : Date.parse(b.aggregate.trade_at ?? '');
      return safeA !== safeB ? safeB - safeA : a.symbol.localeCompare(b.symbol);
    });
  }, [positions]);
}
